import React from 'react';
import useSWR from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { useCurrentUser } from '@/lib/hooks/userData';
import type CustomField from '@/types/api/CustomField';
import type Developer from '@/types/api/Developer';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import { createApiRequest, fetcherWithDataKey } from '@/utils/services';

type PutCustomField = Partial<CustomField>;
type PostCustomField = Partial<CustomField> &
  Pick<CustomField, 'fieldName' | 'fieldType'>;

type PostCustomFieldsRequest = {
  data: {
    ownerOrgId: string;
  } & PostCustomField;
  method: 'post';
  path: 'custom-fields';
  throwErrors: boolean;
};

type PutCustomFieldsRequest = {
  data: {
    ownerOrgId: string;
    updatedBy: Developer;
  } & PutCustomField;
  method: 'put';
  path: `custom-fields/${string}`;
  throwErrors: boolean;
};

type PutCustomFieldsResponse = APIResponse<
  { customField: CustomField } | Record<string, never>
>;

type PostCustomFieldsResponse = APIResponse<
  { customField: CustomField } | Record<string, never>
>;

const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

const useCustomFields = (
  searchParams: object = {},
  swrOptions: object = {},
) => {
  const ownerOrgId = useOwnerOrgId();
  const { user } = useCurrentUser();

  const key =
    searchParams && ownerOrgId
      ? `custom-fields?${joinNonEmptyParams({
          pageSize: 1000,
          ownerOrgId,
          ...searchParams,
        })}`
      : null;
  const { data, error, isValidating, mutate } = useSWR<CustomField[]>(
    key,
    fetcherWithDataKey('customFields'),
    swrOptions,
  );

  // Sort the `fieldOptions` for all custom fields
  const sortedData = React.useMemo(
    () =>
      data?.map((item) =>
        item.fieldOptions
          ? {
              ...item,
              fieldOptions: [...item.fieldOptions].sort(collator.compare),
            }
          : item,
      ),
    [data],
  );

  // The `mutate` calls for `create`, `update`, and `remove` are doing a bit of
  // local state maintenance as Aurora results do not immediately
  // reflect the addition or removal of new custom fields.
  const create = async (fieldData: PostCustomField) => {
    const res = await createApiRequest<
      PostCustomFieldsRequest,
      PostCustomFieldsResponse
    >({
      data: { ownerOrgId, ...fieldData },
      method: 'post',
      path: 'custom-fields',
      throwErrors: true,
    });
    const newCustomField = res.data?.customField;
    if (newCustomField) {
      mutate((oldVal) => [newCustomField, ...(oldVal || [])], {
        revalidate: false,
      });
    }
    return newCustomField;
  };

  const remove = async (customFieldId: string) => {
    await createApiRequest({
      method: 'delete',
      path: `custom-fields/${customFieldId}`,
      throwErrors: true,
    });
    return mutate(
      (oldVal) =>
        (oldVal || []).filter((field) => field.customFieldId !== customFieldId),
      { revalidate: false },
    );
  };

  const update = async (customFieldId: string, patch: PutCustomField) => {
    const res = await createApiRequest<
      PutCustomFieldsRequest,
      PutCustomFieldsResponse
    >({
      data: {
        ownerOrgId,
        updatedBy: {
          displayName: user?.displayName,
          userId: user?.userId,
        },
        ...patch,
      },
      method: 'put',
      path: `custom-fields/${customFieldId}`,
      throwErrors: true,
    });
    const newCustomField = res.data?.customField;
    if (newCustomField) {
      mutate(
        (oldVal) =>
          (oldVal || []).map((field) =>
            field.customFieldId === customFieldId ? newCustomField : field,
          ),
        { revalidate: false },
      );
    }
    return newCustomField;
  };

  return {
    create,
    data: sortedData,
    error,
    isValidating,
    remove,
    update,
  };
};

export default useCustomFields;
